import React, { useState, useRef } from 'react';
import cn from 'classnames';
import { l10n } from '../with-app-root/i18n/i18nUtil';
import './MultiValueInput.css';

interface Props {
  id: string;
  items: string[];
  onRemoveItem: (item: string) => void;
  itemsDisplayLength?: number;
  name?: string;
  value?: string;
  label?: string;
  required?: boolean;
  hidden?: boolean;
  autoComplete?: 'on' | 'off';
  disabled?: boolean;
  placeholder?: string;
  helpText?: string | JSX.Element;
  helpTextPosition?: 'after-label' | 'below-field';
  error?: boolean;
  errorMessage?: string;
  attrs?: React.InputHTMLAttributes<HTMLInputElement>;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

const MultiValueInput: React.FC<Props> = props => {
  const {
    id,
    autoComplete,
    disabled,
    error,
    errorMessage,
    helpText,
    helpTextPosition = 'below-field',
    items,
    itemsDisplayLength,
    label,
    required = false,
    hidden,
    name,
    onBlur,
    onChange,
    onKeyDown,
    onRemoveItem,
    placeholder,
    value,
    attrs
  } = props;

  const [isShowMoreExpanded, setIsShowMoreExpanded] = useState(false);
  const areaRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const itemsLength = !!itemsDisplayLength ? itemsDisplayLength : items.length;

  const onFocusHandler = () => {
    areaRef.current?.classList.add('focus');
  };

  const onBlurHandler = (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    areaRef.current?.classList.remove('focus');
    onBlur && onBlur(e);
  };

  const onAreaClickHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLDivElement).className === 'list') {
      inputRef.current?.focus();
    }
  };

  const onShowMoreClickHandler = () => {
    setIsShowMoreExpanded(prevState => !prevState);
  };

  return (
    <div className="multi-value-input">
      <div
        className={cn('clr-form-control', {
          'clr-form-control-disabled': disabled
        })}
        hidden={hidden}
      >
        {label && (
          <label className="clr-control-label">
            <div className={cn('crc-label-text', { required })}>
              {label}
            </div>
          </label>
        )}
        <div className={cn('clr-control-container', { 'clr-error': error })}>
          <div className="clr-textarea-wrapper">
            <div
              className="clr-textarea"
              aria-describedby={`${id}-subText`}
              aria-invalid={error}
              aria-required={required}
              ref={areaRef}
              onClick={onAreaClickHandler}
            >
              <div className="list">
                {items.slice(0, isShowMoreExpanded ? items.length : itemsLength).map(item => (
                  <span key={item} className="list-item">
                    <span>{item}</span>
                    <button
                      disabled={disabled}
                      onClick={() => onRemoveItem(item)}
                    >
                      <clr-icon shape="times" size="16" />
                    </button>
                  </span>
                ))}
                {!!itemsDisplayLength && items.length > itemsDisplayLength && (
                  <button
                    className="more-btn"
                    onClick={onShowMoreClickHandler}
                  >
                    {!isShowMoreExpanded && (
                      <span>+{items.length - itemsDisplayLength} {l10n('more')}</span>
                    )}
                    <clr-icon
                      shape="angle-double"
                      size="14"
                      title={isShowMoreExpanded ? l10n('collapse') : l10n('expand')}
                      class={cn({ 'collapsed': !isShowMoreExpanded })}
                    />
                  </button>
                )}
                <input
                  id={id}
                  ref={inputRef}
                  type="text"
                  disabled={disabled}
                  placeholder={placeholder}
                  autoComplete={autoComplete}
                  name={name}
                  value={value}
                  onKeyDown={onKeyDown}
                  onChange={onChange}
                  onFocus={onFocusHandler}
                  onBlur={onBlurHandler}
                  title={label === undefined ? placeholder || name : undefined}
                  {...attrs}
                />
              </div>
            </div>
          </div>
          <span className="clr-subtext" id={`${id}-subText`}>
            {error ? (
              <>
                <clr-icon className="clr-validate-icon" shape="exclamation-circle" />
                {errorMessage}
              </>
            ) : (
              <>{helpTextPosition === 'below-field' && helpText}</>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MultiValueInput;
